import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, AlertCircle } from 'lucide-react';

const RefundPolicyPage = () => {
  const lastUpdated = "January 3, 2026";

  return (
    <div data-testid="refund-policy-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-40 pb-16 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] to-[#050505]" />
        <div className="container-custom relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#C5A059] transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F7] mb-4">
            Refund & Cancellation Policy
          </h1>
          <p className="text-[#71717A]">Last Updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-[#050505]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-invert max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-[#A1A1AA] leading-relaxed text-lg">
                  This Refund and Cancellation Policy outlines the terms under which refunds and cancellations are handled 
                  at Eleven11 Studios, a premium web design and development studio serving businesses in the USA and Canada.
                </p>
                <p className="text-[#A1A1AA] leading-relaxed text-lg mt-4">
                  By engaging our services, you acknowledge that you have read and agree to this policy.
                </p>
              </div>

              {/* Important Notice */}
              <div className="bg-[#111111] border border-[#D4AF37]/30 p-6 mb-10 flex gap-4">
                <AlertCircle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-[#F5F5F7] font-semibold mb-2">Important Notice</p>
                  <p className="text-[#A1A1AA]">
                    Due to the nature of digital services, all work is custom-created and cannot be returned or resold. 
                    Once work has begun on your website project, refunds are not guaranteed.
                  </p>
                </div>
              </div>

              {/* Section 1 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">1. Nature of Digital Services</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  Eleven11 Studios provides web design and development services including:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>Custom website design and development</li>
                  <li>Landing page creation</li>
                  <li>E-commerce website development</li>
                  <li>UI/UX design services</li>
                  <li>Website optimization and performance improvements</li>
                  <li>Website deployment and ongoing maintenance</li>
                </ul>
                <p className="text-[#A1A1AA] leading-relaxed mt-4">
                  These services involve significant time, expertise, and resources that are invested immediately upon 
                  project commencement. Unlike physical products, digital work cannot be "returned" once delivered.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">2. Refund Eligibility</h2>
                
                <div className="bg-[#0a0a0a] border border-white/10 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-[#F5F5F7] mb-3">Before Work Begins</h3>
                  <p className="text-[#A1A1AA]">
                    If you cancel your project before any work has commenced, you are eligible for a full refund of 
                    any deposits paid, minus any non-refundable administrative fees (if applicable).
                  </p>
                </div>

                <div className="bg-[#0a0a0a] border border-white/10 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-[#F5F5F7] mb-3">After Work Has Started</h3>
                  <p className="text-[#A1A1AA]">
                    Once work has begun on your website project, refunds are not guaranteed. This includes but is not limited to:
                  </p>
                  <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 ml-4 mt-3">
                    <li>Discovery and planning phases</li>
                    <li>Design mockups and wireframes</li>
                    <li>Development and coding work</li>
                    <li>Any deliverable partially or fully completed</li>
                  </ul>
                </div>

                <div className="bg-[#111111] border border-[#D4AF37]/30 p-6">
                  <h3 className="text-lg font-semibold text-[#D4AF37] mb-3">7-Day Satisfaction Window (New Clients)</h3>
                  <p className="text-[#A1A1AA]">
                    For new clients engaging our services for the first time, we offer a 7-day satisfaction window 
                    from the date of first design deliverable. During this period:
                  </p>
                  <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 ml-4 mt-3">
                    <li>You may request revisions to align work with the agreed scope</li>
                    <li>If you remain unsatisfied after reasonable revision attempts, a partial refund may be issued at our discretion</li>
                    <li>Partial refunds will account for work already completed and resources expended</li>
                  </ul>
                  <p className="text-[#A1A1AA] mt-3 text-sm">
                    This satisfaction window does not apply to rush projects, heavily discounted services, or 
                    situations where the client has failed to provide required materials or feedback.
                  </p>
                </div>
              </div>

              {/* Section 3 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">3. Non-Refundable Items</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  The following are generally non-refundable:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>Third-party costs (domain registration, hosting fees, premium themes/plugins, stock images)</li>
                  <li>Rush fees or expedited service charges</li>
                  <li>Consultation and discovery session fees</li>
                  <li>Completed deliverables that have been approved by the client</li>
                  <li>Custom development work already integrated into your website</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">4. Cancellation Process</h2>
                <div className="bg-[#111111] border border-white/10 p-6 mb-4">
                  <p className="text-[#F5F5F7] font-semibold mb-2">All cancellation requests must be submitted via email to:</p>
                  <a 
                    href="mailto:hello@eleven11-studios.com" 
                    className="text-[#D4AF37] hover:text-[#C5A059] transition-colors"
                  >
                    hello@eleven11-studios.com
                  </a>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  Your cancellation request must include:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>Your full name and business name</li>
                  <li>Project or service being cancelled</li>
                  <li>Reason for cancellation</li>
                  <li>Date of original purchase/agreement</li>
                </ul>
                <p className="text-[#A1A1AA] leading-relaxed mt-4">
                  We will respond to cancellation requests within 2-3 business days and provide information about 
                  any applicable refunds or outstanding balances.
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">5. Refund Processing</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  Approved refunds will be processed within 7-10 business days using the original payment method. 
                  Please note that your bank or payment provider may take additional time to reflect the refund 
                  in your account.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">6. Disputes</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  If you believe you are entitled to a refund not covered by this policy, please contact us. 
                  We are committed to resolving disputes fairly and will review each case individually. 
                  Chargebacks initiated without first attempting to resolve the issue directly with us may 
                  result in suspension of services and collection actions.
                </p>
              </div>

              {/* Section 7 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">7. Our Commitment</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  At Eleven11 Studios, client satisfaction is our priority. While the nature of custom web development 
                  limits our ability to offer unconditional refunds, we are committed to delivering quality work 
                  and will always work with you to address legitimate concerns before they escalate.
                </p>
              </div>

              {/* Contact Section */}
              <div className="bg-[#111111] border border-white/10 p-8 mt-12">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">Questions About This Policy?</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-6">
                  If you have any questions about our Refund and Cancellation Policy, please contact us:
                </p>
                <div className="space-y-3">
                  <p className="text-[#F5F5F7] font-semibold">Eleven11 Studios</p>
                  <p className="text-[#A1A1AA] text-sm">22-23, Block S, DLF Phase 3, Gurugram, Haryana, India 122002</p>
                  <a 
                    href="mailto:hello@eleven11-studios.com" 
                    className="flex items-center gap-2 text-[#D4AF37] hover:text-[#C5A059] transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    hello@eleven11-studios.com
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicyPage;
